/**
 * Holds the page still behind a modal.
 *
 * Two independent modal systems need that at the same time — the
 * server-rendered `<dialog>`s opened through `common/dialogs/dialog` and the React
 * overlays under `common/components/Modal` — and both express it with the
 * `l-prevent-scroll` class on `<body>`.
 *
 * A class carries no count, so whoever closed last would release a lock the
 * other still needs and the page would scroll behind a modal that is still
 * open. Each holder identifies itself instead, and the class follows whether
 * anyone is still holding it. Releasing twice, or releasing something that
 * never acquired, is therefore not a release at all.
 */

const SCROLL_LOCK_CLASS = 'l-prevent-scroll';

const holders = new Set<object>();

function apply(): void {
  // An element that has left the document can no longer release its own hold:
  // a `<dialog>` removed while open never fires `close`. Dropping it here is
  // what keeps one from locking the page for good.
  holders.forEach(holder => {
    if (holder instanceof Element && !holder.isConnected) {
      holders.delete(holder);
    }
  });

  document.body?.classList.toggle(SCROLL_LOCK_CLASS, holders.size > 0);
}

export function acquireScrollLock(holder: object): void {
  holders.add(holder);
  apply();
}

export function releaseScrollLock(holder: object): void {
  holders.delete(holder);
  apply();
}

/** Whether anything currently holds the page. Exported for tests. */
export function isScrollLocked(): boolean {
  return holders.size > 0;
}
